import { Button, UIHeading5, InlineText3, Paragraph3, Tooltip } from '@cian/ui-kit';
import { LinkButton } from '@cian/ui-kit/button';
import React, { useState, useMemo, useCallback, MouseEvent, SyntheticEvent } from 'react';
import { useSelector } from 'react-redux';

import { useEarlyAccessPaywallContext } from '../../../../../../../packages/EarlyAccessPaywall';
import { EAccessCheckTrackingStatus } from '../../../../../../../packages/EarlyAccessPaywall/shared/components/Provider/Provider';
import { ESubscriptionLimitType } from '../../../../../../../packages/EarlyAccessPaywall/shared/types/context';
import { getDynamicCalltrackingPhone } from '../../../../api/dynamicCalltracking';
import { ContentRow } from '../../../../components/ContentRow';
import { useOfferContext } from '../../../../context';
import { IPhone } from '../../../../types/offer';
import { addMortgageTypeToUrl, getMortgageType } from '../../PriceContainer/utils';
import { useGetIsElectronicTrading } from 'shared/hooks/useGetIsElectronicTrading';
import { getTermsValue } from 'shared/json_query/reducers/helpers';
import { selectEarlyAccessContactsFlowEnabled } from 'shared/selectors/earlyAccess/selectEarlyAccessContactsFlowEnabled';
import { useApplicationContext } from 'shared/utils/applicationContext';

const EMPTY_PHONES = [{ countryCode: null, number: null }];

export function AgentPhoneButtonContainer() {
  const {
    offer,
    jsonQuery,
    queryString,
    tracking,
    agentWorkTime,
    agentPhoneWarningMessage,
    isDealRentDisablePhonesExperimentActive,
    user: { isAuthenticated },
    api: { openNotAvailablePopup },
    position,
  } = useOfferContext();
  const { logger, httpApi } = useApplicationContext();
  const isElectronicTrading = useGetIsElectronicTrading();

  const earlyAccessContext = useEarlyAccessPaywallContext();
  const checkContactsAccess = earlyAccessContext?.checkContactsAccess;
  const isEarlyAccessContactsFlowEnabled =
    // TODO: Удалить фича-тоггл
    useSelector(selectEarlyAccessContactsFlowEnabled) && offer.isEarlyAccessEnabled && earlyAccessContext;

  // Получаем выбранные типы ипотеки из фильтров
  const promoSearchTypes = getTermsValue('promo_search_types')(jsonQuery);
  const mortgageType = getMortgageType(promoSearchTypes);

  const getDealRentFormUrl = useMemo(() => {
    const baseUrl = `${offer.fullUrl}?opendealrentform=true`;

    return addMortgageTypeToUrl(baseUrl, mortgageType);
  }, [offer.fullUrl, mortgageType]);

  const [opened, setOpened] = useState(false);
  // dynamic calltracking
  const [comagicLoaded, setComagicLoaded] = useState(false);
  const [comagicLoading, setComagicLoading] = useState(false);
  const [comagicPhone, setComagicPhone] = useState<IPhone | null>(null);

  const phones: IPhone[] = useMemo(() => {
    if (!comagicLoaded || !offer.phones) {
      return EMPTY_PHONES;
    }

    return comagicPhone ? [comagicPhone] : offer.phones;
  }, [comagicLoaded, comagicPhone, offer.phones]);

  const stopPropagation = (e: SyntheticEvent<HTMLElement>) => e.stopPropagation();

  const handleEarlyAccessFlow = useCallback(async () => {
    if (!isAuthenticated) {
      tracking.trackBlockedEarlyAccessPhoneClick(EAccessCheckTrackingStatus.Unauthorized);
    }

    setComagicLoading(true);

    const { isConfirmed, trackingStatus } = await checkContactsAccess({
      offerId: offer.id,
      limitType: ESubscriptionLimitType.PhoneNumber,
    });

    if (isConfirmed) {
      tracking.trackShowEarlyAccessPhoneClick();
      setOpened(true);
    } else if (trackingStatus) {
      tracking.trackBlockedEarlyAccessPhoneClick(trackingStatus);
    }

    setComagicLoading(false);
    setComagicLoaded(true);
  }, [offer, isAuthenticated, tracking, checkContactsAccess]);

  const handleClick = useCallback(
    async (e: MouseEvent<HTMLElement>) => {
      stopPropagation(e);

      if (isEarlyAccessContactsFlowEnabled) {
        await handleEarlyAccessFlow();

        return;
      }

      tracking.trackPhoneOpened(true);
      const { newbuildingDynamicCalltracking, id } = offer;
      const siteBlockId = newbuildingDynamicCalltracking && newbuildingDynamicCalltracking.siteBlockId;
      setComagicLoading(true);
      if (siteBlockId) {
        try {
          const { phone } = await getDynamicCalltrackingPhone({
            httpApi,
            logger,
            blockId: siteBlockId,
            announcementId: id,
            placeType: 'offer',
            dynamicCalltrackingPlaceType: 'sidePanel',
            queryString,
          });

          if (phone) {
            setComagicPhone({ countryCode: phone[0], number: phone.slice(1) });
          }
          // eslint-disable-next-line no-empty
        } catch (err) {}
      }

      if (agentWorkTime) {
        openNotAvailablePopup(offer, position);
      }

      setComagicLoading(false);
      setComagicLoaded(true);
      setOpened(true);
    },
    [
      offer,
      queryString,
      httpApi,
      logger,
      tracking,
      agentWorkTime,
      openNotAvailablePopup,
      position,
      isEarlyAccessContactsFlowEnabled,
      handleEarlyAccessFlow,
    ],
  );

  const trackSafeRentFillFormClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      stopPropagation(e);
      tracking.trackSafeRentFillFormClick(offer.id);
    },
    [tracking, offer],
  );

  const slicedPhone = useMemo(() => {
    return offer.phones && offer.phones[0] ? `${formatPhone(offer.phones[0]).slice(0, -2)}...` : '';
  }, [offer.phones]);

  if (offer.isCianPartner || isElectronicTrading) {
    return null;
  }

  if (isDealRentDisablePhonesExperimentActive) {
    return (
      <Tooltip placement="left" title="Назначить просмотр">
        <LinkButton
          href={getDealRentFormUrl}
          fullWidth
          size="XS"
          onClick={trackSafeRentFillFormClick}
          theme="fill_primary"
        >
          Назначить просмотр
        </LinkButton>
      </Tooltip>
    );
  }

  if (!opened) {
    return (
      <Button
        data-mark="PhoneButton"
        data-name="PhoneButton"
        fullWidth
        size="XS"
        onClick={handleClick}
        theme="fill_primary"
        loading={comagicLoading}
      >
        {slicedPhone}
      </Button>
    );
  }

  return (
    <>
      <ContentRow>
        {phones.map((phone, index) => (
          <ContentRow key={index}>
            <UIHeading5 data-mark="PhoneValue" onClick={stopPropagation}>
              {formatPhone(phone)}
            </UIHeading5>
            {index === phones.length - 1 && agentWorkTime && (
              <Paragraph3 color="text-secondary-default">{agentWorkTime}</Paragraph3>
            )}
          </ContentRow>
        ))}
      </ContentRow>
      {agentPhoneWarningMessage && (
        <ContentRow>
          <InlineText3 data-mark="new-agent-warning" color="text-secondary-default" whiteSpace="pre-line">
            {agentPhoneWarningMessage}
          </InlineText3>
        </ContentRow>
      )}
    </>
  );
}

export function formatPhone(phone: IPhone): string {
  if (!phone || !phone.number) {
    return '';
  }

  return (
    `${phone.countryCode && phone.countryCode.startsWith('+') ? '' : '+'}${phone.countryCode} ` +
    `${phone.number.substr(0, 3)} ` +
    `${phone.number.substr(3, 3)}-${phone.number.substr(6, 2)}-${phone.number.substr(8, 2)}`
  );
}
